import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { hasPermission, surveySchemaPermission } from 'unihow-common';
import styles from '../dimensions.module.css';
import { langType, variableExtraInfoType } from '../../../../constants/types';
import { AVAILABLE_LANGUAGES } from '../../../../constants';
import { getTranslation, withLang } from '../../../../i18n';
import { updateExtraInfoAction, deleteExtraInfoAction } from '../../../../actions';
import ExtraInfoModal from '../ExtraInfoModal';
import { scrollToTop } from '../../../../util';
import ModalDialog from '../../../ModalDialog';

const ExtraInfoManage = ({
  variableExtraInfo, surveyPartCode, dimension, updatedCallback, lang, translate: t
}) => {
  const dispatch = useDispatch();
  const permissions = [...useSelector((state) => state.user.currentUser.permissions)];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleSubmit = (title, info) => {
    setIsModalOpen(false);
    dispatch(updateExtraInfoAction({
      surveyPartCode,
      variableCode: dimension.code,
      title,
      info
    }))
      .then(() => updatedCallback())
      .catch(() => scrollToTop()); // To see the request error message
  };

  const handleDeleteSubmit = () => {
    setIsDeleteModalOpen(false);
    dispatch(deleteExtraInfoAction(variableExtraInfo?.id)).then(() => updatedCallback());
  };

  const dimensionName = getTranslation(dimension.name, lang);
  const userCanEditExtraInfo = hasPermission(permissions, surveySchemaPermission(true));

  const noBottomMargin = { marginBottom: 0 };
  return (
    <>
      {userCanEditExtraInfo
      && (
      <button
        aria-label={t('surveySchemaParts.dimensions.extraInfo.edit')}
        data-cy="open-edit-extra-info-btn"
        onClick={handleOpenModal}
        type="button"
        className="button--outline button--outline--white"
      >{t('surveySchemaParts.dimensions.extraInfo.edit')}
      </button>
      )}
      {isModalOpen
        ? (
          <ExtraInfoModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSubmit={handleSubmit}
            translate={t}
            dimensionName={dimensionName}
            title={variableExtraInfo?.title}
            info={variableExtraInfo?.info}
          />
        ) : null}
      {variableExtraInfo && (
        <>
          {userCanEditExtraInfo && (
          <button onClick={handleOpenDeleteModal} type="button" className={styles.borderlessButton}>{t('surveySchemaParts.dimensions.extraInfo.delete')}</button>
          )}
          <ModalDialog
            isOpen={isDeleteModalOpen}
            onConfirm={handleDeleteSubmit}
            onCancel={handleCloseDeleteModal}
            confirmTranslationKey="common.clear"
            cancelTranslationKey="common.cancel"
            title="surveySchemaParts.dimensions.extraInfo.delete"
            variantButtons
          >
            <div>
              <p style={noBottomMargin}><Translate id="surveySchemaParts.dimensions.extraInfo.deleteConfirmation" /></p>
            </div>
          </ModalDialog>
        </>
      )}
    </>
  );
};

const ExtraInfo = ({
  variableExtraInfo, surveyPartCode, dimension, updatedCallback, lang, translate: t
}) => {
  let key;

  const getInfosByLang = () => AVAILABLE_LANGUAGES.map((language) => {
    const title = variableExtraInfo?.title[language];
    const info = variableExtraInfo?.info[language];

    return (
      <React.Fragment key={`extraInfo${language}`}>
        <label className={styles.extraInfoLang}>{t(`inLanguage.${language}`)}:</label>
        {title && info
          ? (
            <>
              <h3>{title}</h3>
              <div
                dangerouslySetInnerHTML={// eslint-disable-line react/no-danger
                  { __html: info }
                }
              />
            </>
          )
          : <p>{t('surveySchemaParts.dimensions.extraInfo.noExtraInfo')}</p>}
      </React.Fragment>
    );
  });

  return (
    <div className={styles.row} key={key}>
      <div className={styles.label}>
        <p>{t('surveySchemaParts.dimensions.extraInfo.heading')}</p>
      </div>
      <div className={styles.content}>
        <h3>{t('surveySchemaParts.dimensions.extraInfo.title')}</h3>
        <p>{t('surveySchemaParts.dimensions.extraInfo.description')}</p>
        {getInfosByLang()}
        <ExtraInfoManage
          variableExtraInfo={variableExtraInfo}
          dimension={dimension}
          surveyPartCode={surveyPartCode}
          updatedCallback={updatedCallback}
          lang={lang}
          translate={t}
        />
      </div>
    </div>
  );
};

const {
  string, shape, func
} = PropTypes;

ExtraInfoManage.propTypes = {
  variableExtraInfo: variableExtraInfoType,
  surveyPartCode: string.isRequired,
  dimension: shape().isRequired,
  updatedCallback: func.isRequired,
  ...langType
};

ExtraInfoManage.defaultProps = {
  variableExtraInfo: null
};

ExtraInfo.propTypes = {
  variableExtraInfo: variableExtraInfoType,
  surveyPartCode: string.isRequired,
  dimension: shape().isRequired,
  updatedCallback: func.isRequired,
  ...langType
};

ExtraInfo.defaultProps = {
  variableExtraInfo: null
};

export default withLang(ExtraInfo);
